/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiBadge from "components/VuiBadge";

// Icons
import { IoArrowUp, IoArrowDown } from "react-icons/io5";

export default function data(transactions = []) {
  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return Math.abs(amount).toFixed(8);
  };

  const getTypeInfo = (category) => {
    switch(category) {
      case 'send':
        return { 
          icon: <IoArrowUp size="16px" color="#FF0080" />, 
          text: 'Send',
          color: 'error'
        };
      case 'receive':
        return { 
          icon: <IoArrowDown size="16px" color="#00CED1" />, 
          text: 'Receive',
          color: 'success'
        };
      case 'generate':
        return { 
          icon: <IoArrowDown size="16px" color="#FFD700" />, 
          text: 'Mining',
          color: 'warning'
        };
      default:
        return { 
          icon: <IoArrowDown size="16px" color="#00CED1" />, 
          text: category,
          color: 'info'
        };
    }
  };

  const getStatusColor = (confirmations) => {
    if (confirmations === 0) return 'warning';
    if (confirmations < 6) return 'info';
    return 'success';
  };

  const getStatusText = (confirmations) => {
    if (confirmations === 0) return 'Pending';
    if (confirmations < 6) return `${confirmations} Conf`;
    return 'Confirmed';
  };

  const rows = transactions.map((tx) => {
    const typeInfo = getTypeInfo(tx.category);
    
    // Ensure txid is a string
    const txidStr = tx.txid ? String(tx.txid) : '';
    const shortTxid = txidStr && txidStr.length >= 16 
      ? `${txidStr.substring(0, 8)}...${txidStr.substring(txidStr.length - 8)}`
      : txidStr || 'N/A';
    
    return {
      txid: (
        <VuiBox display="flex" alignItems="center">
          <VuiTypography
            component="a"
            href={txidStr ? `https://explorer.mytherrablockchain.org/tx/${txidStr}` : '#'}
            target="_blank"
            rel="noopener noreferrer"
            variant="button"
            color="info"
            fontWeight="medium"
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': {
                color: '#00CED1',
                textDecoration: 'underline',
              }
            }}
          >
            {shortTxid}
          </VuiTypography>
        </VuiBox>
      ),
      type: (
        <VuiBox display="flex" alignItems="center">
          {typeInfo.icon}
          <VuiTypography pl="8px" variant="button" color={typeInfo.color} fontWeight="medium">
            {typeInfo.text}
          </VuiTypography>
        </VuiBox>
      ),
      amount: (
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {formatAmount(tx.amount)} MYT
        </VuiTypography>
      ),
      address: (
        <VuiTypography variant="caption" color="text">
          {tx.address ? `${tx.address.substring(0, 12)}...` : 'N/A'}
        </VuiTypography>
      ),
      time: (
        <VuiTypography variant="caption" color="text">
          {formatDate(tx.time)}
        </VuiTypography>
      ),
      status: (
        <VuiBadge
          variant="standard"
          badgeContent={getStatusText(tx.confirmations)}
          color={getStatusColor(tx.confirmations)}
          size="xs"
          container
        />
      ),
    };
  });

  return {
    columns: [
      { name: "txid", align: "left" },
      { name: "type", align: "left" },
      { name: "amount", align: "left" },
      { name: "time", align: "left" },
      { name: "status", align: "center" },
    ],
    rows,
  };
}
